import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField,
  MenuItem, Alert, CircularProgress, Chip, Divider, Stack,
  Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { userAPI, projectAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MONTHS = [
  { value: '1', label: 'January' },  { value: '2', label: 'February' },
  { value: '3', label: 'March' },    { value: '4', label: 'April' },
  { value: '5', label: 'May' },      { value: '6', label: 'June' },
  { value: '7', label: 'July' },     { value: '8', label: 'August' },
  { value: '9', label: 'September' },{ value: '10', label: 'October' },
  { value: '11', label: 'November' },{ value: '12', label: 'December' },
];

const STATUS_COLORS = {
  'Completed':   '#2E7D32',
  'In Progress': '#1565C0',
  'Blocked':     '#C62828',
  'On Hold':     '#E65100',
  'Review':      '#7B1FA2',
};

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 3 }, (_, i) => String(currentYear - i));
const todayStr = new Date().toISOString().split('T')[0];

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

const formatDate = (d) => {
  if (!d) return '';
  const parts = d.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d;
};

// ── Preview Table matching exact Excel format ─────────────────────────────────
function PreviewTable({ statuses }) {
  const byDate = {};
  statuses.forEach(s => {
    if (!byDate[s.date]) byDate[s.date] = [];
    byDate[s.date].push(s);
  });
  const sortedDates = Object.keys(byDate).sort();
  let srNo = 0;

  const hSx = {
    bgcolor: '#D9E1F2', fontWeight: 700, color: '#1E3A5F',
    fontSize: '0.72rem', border: '1px solid #aab', textAlign: 'center',
    py: 1, px: 1, whiteSpace: 'pre-line', lineHeight: 1.3,
  };
  const cSx = { border: '1px solid #ddd', px: 1, py: 0.8, fontSize: '0.8rem', verticalAlign: 'middle' };

  return (
    <TableContainer sx={{ border: '1px solid #aaa', mt: 1, overflowX: 'auto' }}>
      <Table size="small" sx={{ borderCollapse: 'collapse', minWidth: 900 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...hSx, minWidth: 40 }}>{'Sr.\nNo'}</TableCell>
            <TableCell sx={{ ...hSx, minWidth: 90 }}>Date</TableCell>
            <TableCell sx={{ ...hSx, minWidth: 120 }}>{'Project/Client\nName'}</TableCell>
            <TableCell sx={{ ...hSx, minWidth: 280 }}>{'Actual Working/Task\n(Description)'}</TableCell>
            <TableCell sx={{ ...hSx, minWidth: 130 }}>{'Issues/\nChallenges'}</TableCell>
            <TableCell sx={{ ...hSx, minWidth: 110 }}>{'Start and End Date\n(Hours Involved)'}</TableCell>
            <TableCell sx={{ ...hSx, minWidth: 120 }}>{'Whether project met\ndeadline or not'}</TableCell>
            <TableCell sx={{ ...hSx, minWidth: 90 }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedDates.map(date => {
            const rows = byDate[date];
            srNo++;
            return rows.map((s, idx) => {
              const taskStatus = s.tasks_planned || 'In Progress';
              const stColor = STATUS_COLORS[taskStatus] || '#000';
              const isOverdue = s.Project?.end_date && new Date(s.date) > new Date(s.Project.end_date);
              const deadlineColor = isOverdue ? '#C62828' : '#2E7D32';
              return (
                <TableRow key={s.id}>
                  {idx === 0 && (
                    <>
                      <TableCell rowSpan={rows.length} sx={{ ...cSx, textAlign: 'center', fontWeight: 700, color: '#555', bgcolor: '#FAFAFA' }}>{srNo}</TableCell>
                      <TableCell rowSpan={rows.length} sx={{ ...cSx, textAlign: 'center', fontWeight: 600, whiteSpace: 'nowrap', bgcolor: '#FAFAFA' }}>{formatDate(date)}</TableCell>
                    </>
                  )}
                  <TableCell sx={{ ...cSx, fontWeight: 700, textAlign: 'center' }}>{s.Project?.name || '—'}</TableCell>
                  <TableCell sx={{ ...cSx, textAlign: 'center' }}>{s.tasks_completed}</TableCell>
                  <TableCell sx={{ ...cSx, color: s.blockers && s.blockers !== 'None' ? '#C62828' : '#aaa' }}>
                    {s.blockers && s.blockers !== 'None' ? s.blockers : ''}
                  </TableCell>
                  <TableCell sx={{ ...cSx, textAlign: 'center', fontWeight: 600 }}>{s.start_time && s.end_time ? `${s.start_time} - ${s.end_time}` : '—'}</TableCell>
                  <TableCell sx={{ ...cSx, textAlign: 'center', fontWeight: 700, color: deadlineColor }}>{isOverdue ? 'No' : 'Yes'}</TableCell>
                  <TableCell sx={{ ...cSx, textAlign: 'center', fontWeight: 600, color: stColor }}>{taskStatus}</TableCell>
                </TableRow>
              );
            });
          })}
          {statuses.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>No data found for this period.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Daily Preview Table ───────────────────────────────────────────────────────
function DailyPreviewTable({ statuses, date }) {
  const hSx = {
    bgcolor: '#D9E1F2', fontWeight: 700, color: '#1E3A5F',
    fontSize: '0.72rem', border: '1px solid #aab', textAlign: 'center',
    py: 1, px: 1, whiteSpace: 'pre-line', lineHeight: 1.3,
  };
  const cSx = { border: '1px solid #ddd', px: 1, py: 0.8, fontSize: '0.8rem', verticalAlign: 'middle' };

  return (
    <TableContainer sx={{ border: '1px solid #aaa', mt: 1, overflowX: 'auto' }}>
      <Table size="small" sx={{ borderCollapse: 'collapse', minWidth: 800 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...hSx, minWidth: 40 }}>{'Sr.\nNo'}</TableCell>
            <TableCell sx={{ ...hSx, minWidth: 120 }}>{'Project/Client\nName'}</TableCell>
            <TableCell sx={{ ...hSx, minWidth: 150 }}>{'Actual\nWorking/Task'}</TableCell>
            <TableCell sx={{ ...hSx, minWidth: 200 }}>Description</TableCell>
            <TableCell sx={{ ...hSx, minWidth: 130 }}>{'Issues/\nChallenges'}</TableCell>
            <TableCell sx={{ ...hSx, minWidth: 110 }}>{'Hours\nInvolved'}</TableCell>
            <TableCell sx={{ ...hSx, minWidth: 120 }}>{'Whether project met\ndeadline or not'}</TableCell>
            <TableCell sx={{ ...hSx, minWidth: 90 }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {statuses.map((s, i) => {
            const taskStatus = s.tasks_planned || 'In Progress';
            const stColor = STATUS_COLORS[taskStatus] || '#000';
            const isOverdue = s.Project?.end_date && new Date(s.date) > new Date(s.Project.end_date);
            return (
              <TableRow key={s.id}>
                <TableCell sx={{ ...cSx, textAlign: 'center', fontWeight: 700, color: '#555' }}>{i + 1}</TableCell>
                <TableCell sx={{ ...cSx, fontWeight: 700, textAlign: 'center' }}>{s.Project?.name || '—'}</TableCell>
                <TableCell sx={{ ...cSx, fontWeight: 700, textAlign: 'center' }}>{s.tasks_completed}</TableCell>
                <TableCell sx={{ ...cSx, color: 'text.secondary' }}></TableCell>
                <TableCell sx={{ ...cSx, color: s.blockers && s.blockers !== 'None' ? '#C62828' : '#aaa' }}>
                  {s.blockers && s.blockers !== 'None' ? s.blockers : ''}
                </TableCell>
                <TableCell sx={{ ...cSx, textAlign: 'center', fontWeight: 600 }}>{s.start_time && s.end_time ? `${s.start_time} - ${s.end_time}` : '—'}</TableCell>
                <TableCell sx={{ ...cSx, textAlign: 'center', fontWeight: 700, color: isOverdue ? '#C62828' : '#2E7D32' }}>{isOverdue ? 'No' : 'Yes'}</TableCell>
                <TableCell sx={{ ...cSx, textAlign: 'center', fontWeight: 600, color: stColor }}>{taskStatus}</TableCell>
              </TableRow>
            );
          })}
          {statuses.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>No data found for {date}.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ReportsPage() {
  const { user } = useAuth();
  const isAdmin = ['admin', 'higher_authority', 'support_team'].includes(user?.role);
  const token = localStorage.getItem('token');

  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState('');

  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [year, setYear] = useState(String(currentYear));
  const [mUser, setMUser] = useState('');
  const [mProject, setMProject] = useState('');
  const [mPreview, setMPreview] = useState(null);
  const [mLoading, setMLoading] = useState(false);
  const [mDl, setMDl] = useState(false);

  const [dailyDate, setDailyDate] = useState(todayStr);
  const [dUser, setDUser] = useState('');
  const [dProject, setDProject] = useState('');
  const [dPreview, setDPreview] = useState(null);
  const [dLoading, setDLoading] = useState(false);
  const [dDl, setDDl] = useState(false);

  useEffect(() => {
    if (isAdmin) userAPI.getAll().then(r => setUsers(r.data)).catch(() => {});
    projectAPI.getAll().then(r => setProjects(r.data.projects || [])).catch(() => {});
  }, []);

  const getParams = (extra = {}) => {
    const p = new URLSearchParams(extra);
    return p;
  };

  const triggerDownload = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const a = document.createElement('a');
    a.href = url; a.setAttribute('download', filename);
    document.body.appendChild(a); a.click();
    a.remove(); window.URL.revokeObjectURL(url);
  };

  const loadMonthly = async () => {
    setError(''); setMLoading(true);
    try {
      const p = getParams({ month, year });
      if (mUser) p.append('user_id', mUser);
      if (mProject) p.append('project_id', mProject);
      const r = await axios.get(`${API_URL}/reports/monthly-data?${p}`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      setMPreview(r.data);
    } catch { setError('Failed to load preview'); }
    finally { setMLoading(false); }
  };

  const downloadMonthly = async () => {
    setMDl(true); setError('');
    try {
      const p = getParams({ month, year });
      if (mUser) p.append('user_id', mUser);
      if (mProject) p.append('project_id', mProject);
      const r = await axios.get(`${API_URL}/reports/monthly?${p}`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true, responseType: 'blob' });
      const mn = MONTHS.find(m => m.value === month)?.label;
      triggerDownload(r.data, `${mn}_Month_Report_${year}.xlsx`);
    } catch { setError('Download failed'); }
    finally { setMDl(false); }
  };

  const loadDaily = async () => {
    setError(''); setDLoading(true);
    try {
      const p = getParams({ date: dailyDate });
      if (dUser) p.append('user_id', dUser);
      if (dProject) p.append('project_id', dProject);
      const r = await axios.get(`${API_URL}/reports/daily-data?${p}`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      setDPreview(r.data);
    } catch { setError('Failed to load preview'); }
    finally { setDLoading(false); }
  };

  const downloadDaily = async () => {
    setDDl(true); setError('');
    try {
      const p = getParams({ date: dailyDate });
      if (dUser) p.append('user_id', dUser);
      if (dProject) p.append('project_id', dProject);
      const r = await axios.get(`${API_URL}/reports/daily?${p}`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true, responseType: 'blob' });
      triggerDownload(r.data, `DailyReport_${dailyDate}.xlsx`);
    } catch { setError('Download failed'); }
    finally { setDDl(false); }
  };

  const FilterBar = ({ children }) => (
    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, mb: 3 }}>
      <Grid container spacing={2} alignItems="center">{children}</Grid>
    </Box>
  );

  const PreviewHeader = ({ title, sub, onDownload, downloading }) => (
    <Box sx={{ bgcolor: '#1E3A5F', px: 3, py: 1.5, borderRadius: 2, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontSize: '0.65rem' }}>{title}</Typography>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 800 }}>VERSATILE PRIME INFOSOFT PVT. LTD.</Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{sub}</Typography>
      </Box>
      <Button variant="outlined" size="small" startIcon={<DownloadIcon />}
        sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff' } }}
        onClick={onDownload} disabled={downloading}>Download Excel</Button>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>Reports</Typography>
        <Typography color="text.secondary" variant="body2">Generate Excel reports in Versatile Prime Infosoft format</Typography>
      </Box>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(_, v) => { setTab(v); setError(''); }}>
            <Tab icon={<CalendarMonthIcon fontSize="small" />} iconPosition="start" label="Monthly Report" />
            <Tab icon={<CalendarTodayIcon fontSize="small" />} iconPosition="start" label="Daily Report" />
          </Tabs>
        </Box>

        <CardContent>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          {/* Monthly */}
          <TabPanel value={tab} index={0}>
            <FilterBar>
              <Grid item xs={6} sm={3} md={2}>
                <TextField fullWidth select label="Month" value={month} size="small" onChange={e => setMonth(e.target.value)}>
                  {MONTHS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={2} md={2}>
                <TextField fullWidth select label="Year" value={year} size="small" onChange={e => setYear(e.target.value)}>
                  {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
              </Grid>
              {isAdmin && (
                <Grid item xs={12} sm={4} md={3}>
                  <TextField fullWidth select label="Employee" value={mUser} size="small" onChange={e => setMUser(e.target.value)}>
                    <MenuItem value="">All Employees</MenuItem>
                    {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12} sm={4} md={3}>
                <TextField fullWidth select label="Project" value={mProject} size="small" onChange={e => setMProject(e.target.value)}>
                  <MenuItem value="">All Projects</MenuItem>
                  {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Stack direction="row" spacing={1}>
                  <Button fullWidth variant="outlined" size="small" sx={{ height: 40 }} onClick={loadMonthly} disabled={mLoading}>
                    {mLoading ? <CircularProgress size={16} /> : 'Preview'}
                  </Button>
                  <Button fullWidth variant="contained" size="small" sx={{ height: 40 }}
                    startIcon={mDl ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                    onClick={downloadMonthly} disabled={mDl}>Excel</Button>
                </Stack>
              </Grid>
            </FilterBar>

            {mPreview && (
              <>
                <PreviewHeader
                  title="Monthly Report Preview"
                  sub={`Month: ${MONTHS.find(m => m.value === month)?.label} ${year}  ·  Entries: ${mPreview.total}`}
                  onDownload={downloadMonthly} downloading={mDl}
                />
                <PreviewTable statuses={mPreview.statuses} />

              </>
            )}
            {!mPreview && !mLoading && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CalendarMonthIcon sx={{ fontSize: 56, opacity: 0.15, display: 'block', mx: 'auto', mb: 1 }} />
                <Typography color="text.secondary">Select month and year, then click Preview or Excel</Typography>
              </Box>
            )}
          </TabPanel>

          {/* Daily */}
          <TabPanel value={tab} index={1}>
            <FilterBar>
              <Grid item xs={12} sm={3} md={2}>
                <TextField fullWidth label="Date" type="date" value={dailyDate} size="small"
                  onChange={e => setDailyDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              {isAdmin && (
                <Grid item xs={12} sm={4} md={3}>
                  <TextField fullWidth select label="Employee" value={dUser} size="small" onChange={e => setDUser(e.target.value)}>
                    <MenuItem value="">All Employees</MenuItem>
                    {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12} sm={4} md={3}>
                <TextField fullWidth select label="Project" value={dProject} size="small" onChange={e => setDProject(e.target.value)}>
                  <MenuItem value="">All Projects</MenuItem>
                  {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <Stack direction="row" spacing={1}>
                  <Button fullWidth variant="outlined" size="small" sx={{ height: 40 }} onClick={loadDaily} disabled={dLoading}>
                    {dLoading ? <CircularProgress size={16} /> : 'Preview'}
                  </Button>
                  <Button fullWidth variant="contained" size="small" sx={{ height: 40 }}
                    startIcon={dDl ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                    onClick={downloadDaily} disabled={dDl}>Excel</Button>
                </Stack>
              </Grid>
            </FilterBar>

            {dPreview && (
              <>
                <PreviewHeader
                  title="Daily Report Preview"
                  sub={`Date: ${formatDate(dailyDate)}  ·  Entries: ${dPreview.total}`}
                  onDownload={downloadDaily} downloading={dDl}
                />
                <DailyPreviewTable statuses={dPreview.statuses} date={dailyDate} />
              </>
            )}
            {!dPreview && !dLoading && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CalendarTodayIcon sx={{ fontSize: 56, opacity: 0.15, display: 'block', mx: 'auto', mb: 1 }} />
                <Typography color="text.secondary">Select a date, then click Preview or Excel</Typography>
              </Box>
            )}
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
}